import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import {
  openDatabaseObjectModal,
  openErrorDetailsModal,
  openEmailModal,
} from "../../store/adminSlice";
import type { ActionLogInfo } from "@services/admin/types";

/**
 * Custom hook that provides all action log row handlers
 * Reusable across components that display action logs
 */
export function useActionLogHandlers() {
  const dispatch = useDispatch<AppDispatch>();

  const handleViewEmail = (emailId: string) => {
    dispatch(openEmailModal(emailId));
  };

  const handleViewDetails = (log: ActionLogInfo) => {
    // Open the action log in database object modal
    dispatch(
      openDatabaseObjectModal({ table: "dobbyActionLog", id: log.id })
    );
  };

  const handleViewGptRequest = (gptRequestId: string) => {
    dispatch(
      openDatabaseObjectModal({ table: "gptRequest", id: gptRequestId })
    );
  };

  const handleViewExtractedData = (extractedDataId: string) => {
    dispatch(
      openDatabaseObjectModal({ table: "extractedData", id: extractedDataId })
    );
  };

  const handleViewDerivedData = (derivedDataId: string) => {
    dispatch(
      openDatabaseObjectModal({ table: "derivedData", id: derivedDataId })
    );
  };

  const handleViewShipmentEmail = (shipmentEmailId: string) => {
    dispatch(
      openDatabaseObjectModal({ table: "shipmentEmail", id: shipmentEmailId })
    );
  };

  const handleViewShipmentFeed = (shipmentFeedId: string) => {
    dispatch(
      openDatabaseObjectModal({ table: "shipmentFeed", id: shipmentFeedId })
    );
  };

  const handleViewAIAgentTrace = (log: ActionLogInfo) => {
    // Try to find the trace URL in the log's extra data
    const traceUrl =
      log.extra?.openai_trace_url ||
      log.extra?.trace_url ||
      log.extra?.traceUrl;

    if (traceUrl) {
      // Open the trace URL in a new window
      window.open(traceUrl, "_blank", "noopener,noreferrer");
    } else {
      // If no direct URL, show database object modal as fallback
      dispatch(
        openDatabaseObjectModal({ table: "dobbyActionLog", id: log.id })
      );
    }
  };

  const handleViewErrors = (log: ActionLogInfo) => {
    dispatch(openErrorDetailsModal(log));
  };

  const handleViewReminder = (reminderId: string) => {
    dispatch(
      openDatabaseObjectModal({ table: "shipmentReminder", id: reminderId })
    );
  };

  const handleViewEmailAutomationResult = (resultId: string) => {
    window.open(`/www/admin/email-automation/result/${resultId}`, "_blank");
  };

  return {
    handleViewEmail,
    handleViewDetails,
    handleViewGptRequest,
    handleViewExtractedData,
    handleViewDerivedData,
    handleViewShipmentEmail,
    handleViewShipmentFeed,
    handleViewAIAgentTrace,
    handleViewErrors,
    handleViewReminder,
    handleViewEmailAutomationResult,
  };
}